import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Switch } from '@/shared/ui/switch';
import { Eye, EyeOff, Loader2, CopyIcon } from 'lucide-react';
import {
  authTypeRequiresPassword,
  BasicServiceSet,
  buildBssAuth,
  CreateRouterConfigData,
  createDefaultRouterConfigSettings,
  getBssAuthType,
  getBssPassword,
  parseRouterConfigSettings,
  RouterConfig,
  RouterConfigSettings,
  sanitizeBasicServiceSet,
  sanitizeRouterConfigSettings,
  WifiAuthType,
  WifiNetwork,
  WifiRfBand,
} from '@/shared/types/routerTypes';
import { useCreateRouterConfig } from '@/domains/routers/services/account-routers';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { showError, showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import { Separator } from '@/shared/ui/separator';
import { Select, SelectValue, SelectContent, SelectItem, SelectTrigger } from '@/shared/ui/select';

export type RouterConfigDialogMode = 'create' | 'duplicate' | 'view';

interface RouterConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  mode?: RouterConfigDialogMode;
  /** Source config for view or duplicate modes. */
  config?: RouterConfig | null;
  /** View mode: open create dialog prefilled from this config. */
  onDuplicate?: () => void;
}

type BooleanFlagKey = {
  [K in keyof RouterConfigSettings]: RouterConfigSettings[K] extends boolean ? K : never;
}[keyof RouterConfigSettings];

interface FlagRow {
  key: BooleanFlagKey;
  label: string;
  description?: string;
}

const emptyBss = (band: WifiRfBand, type: WifiAuthType = WifiAuthType.WPA2): BasicServiceSet => ({
  ssid: '',
  band,
  hidden: false,
  ...buildBssAuth(type),
});

const defaultNetwork = (): WifiNetwork => ({
  basicServiceSets: [emptyBss(WifiRfBand.RF_2GHZ), emptyBss(WifiRfBand.RF_5GHZ)],
  clientIsolation: false,
});

const defaultSettings = (): RouterConfigSettings => ({
  ...createDefaultRouterConfigSettings(),
  networks: [defaultNetwork()],
});

const defaultForm = (): CreateRouterConfigData => ({
  nickname: '',
  routerConfigJson: JSON.stringify(defaultSettings()),
});

const MODE_FLAGS: FlagRow[] = [
  { key: 'disablePendingUpdateReboot', label: 'Disable Pending Update Reboot' },
  { key: 'bypassMode', label: 'Bypass Mode', description: '' },
  { key: 'outdoorMode', label: 'Outdoor Mode' },
  { key: 'dfsEnabled', label: 'DFS Enabled' },
  { key: 'disable5ghzHigh', label: 'Disable 5 GHz High Radio' },
];

const isValidPassword = (required: boolean, password?: string | null) => {
  if (!password) return !required;
  return password.length >= 8 && password.length <= 63;
};

const isValidSsid = (ssid?: string | null) => !!ssid && ssid.trim().length > 0 && ssid.trim().length <= 32;

const getBss = (network: WifiNetwork | undefined, band: WifiRfBand): BasicServiceSet => {
  return network?.basicServiceSets?.find((bss) => bss.band === band) ?? emptyBss(band);
};

const withUpdatedBss = (
  settings: RouterConfigSettings,
  networkIndex: number,
  band: WifiRfBand,
  patch: Partial<BasicServiceSet>,
): RouterConfigSettings => {
  const networks = settings.networks ? [...settings.networks] : [defaultNetwork()];
  while (networks.length <= networkIndex) {
    networks.push(defaultNetwork());
  }
  const network = networks[networkIndex] ?? defaultNetwork();
  const existing = network.basicServiceSets ?? [];
  const otherBands = existing.filter((bss) => bss.band !== band);
  const current = getBss(network, band);
  const merged = { ...current, ...patch, band };
  networks[networkIndex] = {
    ...network,
    basicServiceSets: [...otherBands, sanitizeBasicServiceSet(merged)],
  };
  return {
    ...settings,
    networks,
  };
};

const duplicateNickname = (nickname: string | null | undefined) => {
  const base = nickname?.trim() || 'Unnamed config';
  return base.endsWith(' (copy)') ? `${base} 2` : `${base} (copy)`;
};

const RouterConfigDialog = ({
  open,
  onOpenChange,
  onSuccess,
  mode = 'create',
  config,
  onDuplicate,
}: RouterConfigDialogProps) => {
  const isView = mode === 'view';
  const isDuplicate = mode === 'duplicate';
  const createConfig = useCreateRouterConfig();
  const [form, setForm] = useState<CreateRouterConfigData>(defaultForm());
  const [splitBands, setSplitBands] = useState(true);
  const [activeNetworkIndex, setActiveNetworkIndex] = useState(0);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!open) return;

    if (config && (isView || isDuplicate)) {
      const settings = parseRouterConfigSettings(config.routerConfigJson) ?? defaultSettings();
      setForm({
        nickname: isDuplicate ? duplicateNickname(config.nickname) : config.nickname,
        routerConfigJson: JSON.stringify({ ...defaultSettings(), ...settings }),
      });
      const bss2 = getBss(settings.networks?.[0], WifiRfBand.RF_2GHZ);
      const bss5 = getBss(settings.networks?.[0], WifiRfBand.RF_5GHZ);
      setSplitBands(Boolean(bss2?.ssid && bss5?.ssid && bss2.ssid !== bss5.ssid));
    } else {
      setForm(defaultForm());
      setSplitBands(true);
    }
    setActiveNetworkIndex(0);
    setShowPassword(false);
  }, [open, mode, config?.configId]);

  const pending = createConfig.isPending;
  const readOnly = isView || pending;
  const settings = parseRouterConfigSettings(form.routerConfigJson) ?? defaultSettings();
  const activeNetwork = settings.networks?.[activeNetworkIndex] ?? defaultNetwork();
  const bss2 = getBss(activeNetwork, WifiRfBand.RF_2GHZ);
  const bss5 = getBss(activeNetwork, WifiRfBand.RF_5GHZ);

  const updateSettings = (updater: (current: RouterConfigSettings) => RouterConfigSettings) => {
    setForm((prev) => {
      const current = parseRouterConfigSettings(prev.routerConfigJson) ?? defaultSettings();
      return { ...prev, routerConfigJson: JSON.stringify(updater(current)) };
    });
  };

  const setFlag = (key: BooleanFlagKey, value: boolean) => {
    updateSettings((current) => ({ ...current, [key]: value }));
  };

  const updateBss = (band: WifiRfBand, patch: Partial<BasicServiceSet>) => {
    updateSettings((current) => withUpdatedBss(current, activeNetworkIndex, band, patch));
  };

  const updateBothBands = (patch: Partial<BasicServiceSet>) => {
    updateSettings((current) => {
      const next = withUpdatedBss(
        withUpdatedBss(current, activeNetworkIndex, WifiRfBand.RF_2GHZ, patch),
        activeNetworkIndex,
        WifiRfBand.RF_5GHZ,
        patch,
      );
      return next;
    });
  };

  const updateSecurityType = (type: WifiAuthType) => {
    const password = getBssPassword(bss2);
    updateBothBands(buildBssAuth(type, password));
  };

  const updateSharedPassword = (password: string) => {
    updateBothBands(buildBssAuth(getBssAuthType(bss2), password));
  };

  const updateHiddenBoth = (hidden: boolean) => {
    updateBothBands({ hidden });
  };

  /**
  const addNetwork = () => {
    const nextIndex = settings.networks?.length ?? 0;
    updateSettings((current) => ({
      ...current,
      networks: [...(current.networks ?? []), defaultNetwork()],
    }));
    setActiveNetworkIndex(nextIndex);
  };
  */

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isView) return;
    if (!form.nickname.trim()) {
      showError(ERROR.ROUTER.ENTER_NAME);
      return;
    }

    if (settings.applyNetworks) {
      for (const network of settings.networks) {
        const nBss2 = getBss(network, WifiRfBand.RF_2GHZ);
        const nBss5 = getBss(network, WifiRfBand.RF_5GHZ);
        if (!isValidSsid(nBss2.ssid) || !isValidSsid(nBss5.ssid)) {
          showError(ERROR.ROUTER.ENTER_SSID);
          return;
        }
        const authType = getBssAuthType(nBss2);
        if (authTypeRequiresPassword(authType)) {
          const password = getBssPassword(nBss2);
          if (!isValidPassword(true, password)) {
            showError(ERROR.ROUTER.ENTER_PASSWORD);
            return;
          }
        }
      }
    }

    const payload: CreateRouterConfigData = {
      nickname: form.nickname.trim(),
      routerConfigJson: JSON.stringify(sanitizeRouterConfigSettings(settings)),
    };

    try {
      await createConfig.mutateAsync(payload);
      showSuccess(SUCCESS.ROUTER.CONFIG_CREATED);
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      showErrorWithApiError(error, ERROR.ROUTER.CREATE_CONFIG);
    }
  };

  const renderFlag = ({ key, label, description }: FlagRow) => (
    <div key={key} className="flex items-center justify-between gap-3 rounded-lg border p-3">
      <div className="min-w-0">
        <Label htmlFor={key}>{label}</Label>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </div>
      <Switch
        id={key}
        checked={Boolean(settings[key])}
        onCheckedChange={(checked) => setFlag(key, checked)}
        disabled={readOnly}
      />
    </div>
  );

  const activeAuthType = getBssAuthType(bss2);
  const sharedPassword = getBssPassword(bss2);
  const showPasswordField = authTypeRequiresPassword(activeAuthType);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[720px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isView ? 'Wi-Fi Config Details' : isDuplicate ? 'Duplicate Wi-Fi Config' : 'Create Wi-Fi Config'}
          </DialogTitle>
          <DialogDescription>
            {isView
              ? 'Review this saved Wi-Fi / bypass profile.'
              : isDuplicate
                ? 'Create a new Wi-Fi config from an existing one. Review and adjust settings before saving.'
                : 'Capture router configuration fields (networks, mode, and advanced apply flags).'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3">
          {isView && config && (
            <p className="text-xs text-muted-foreground font-mono break-all">{config.configId}</p>
          )}
          <div className="space-y-2">
            <Label htmlFor="config-name">Nickname</Label>
            <Input
              id="config-name"
              value={form.nickname}
              onChange={(e) => setForm((prev) => ({ ...prev, nickname: e.target.value }))}
              placeholder="e.g. nickname"
              disabled={readOnly}
            />
          </div>

          <section className="space-y-3 rounded-lg border p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold">WiFi Networks</h3>
                <p className="text-xs text-muted-foreground">Set up basic service sets for 2.4 / 5 GHz with your chosen Wi-Fi security.</p>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
              <div className="flex flex-wrap items-center gap-2">
                {(settings.networks ?? []).map((_, idx) => (
                  <Button
                    key={idx}
                    type="button"
                    size="sm"
                    variant={idx === activeNetworkIndex ? 'default' : 'outline'}
                    onClick={() => setActiveNetworkIndex(idx)}
                    disabled={readOnly}
                  >
                    Network {idx + 1}
                  </Button>
                ))}
              </div>
              {/*
              TODO: Uncomment this when we have a way to add more networks
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2"
                disabled={readOnly}
                onClick={addNetwork}
              >
                <Plus className="h-4 w-4" />
                Add Network
              </Button> */}
            </div>

            <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
              <div>
                <Label htmlFor="splitBands">Split 2.4/5 GHz Bands</Label>
                <p className="text-xs text-muted-foreground">When enabled, you can set separate Network Names for 2.4 GHz and 5 GHz.</p>
              </div>
              <Switch
                id="splitBands"
                checked={splitBands}
                onCheckedChange={(checked) => {
                  setSplitBands(checked);
                  if (!checked) {
                    const commonName = bss2.ssid;
                    updateBothBands({
                      ssid: commonName,
                      ...buildBssAuth(activeAuthType, sharedPassword),
                    });
                  }
                }}
                disabled={readOnly}
              />
            </div>

            <div className="space-y-3 rounded-lg border p-3">
              {splitBands ? (
                <>
                  <div className="grid grid-cols-2 items-center gap-4">
                    <Label className="text-left">Network Name 2.4 GHz</Label>
                    <Input
                      value={bss2.ssid}
                      onChange={(e) => updateBss(WifiRfBand.RF_2GHZ, { ssid: e.target.value })}
                      placeholder="e.g. Test"
                      maxLength={32}
                      disabled={readOnly}
                    />
                  </div>
                  <div className="grid grid-cols-2 items-center gap-4">
                    <Label className="text-left">Network Name 5 GHz</Label>
                    <Input
                      value={bss5.ssid}
                      onChange={(e) => updateBss(WifiRfBand.RF_5GHZ, { ssid: e.target.value })}
                      placeholder="e.g. Test"
                      maxLength={32}
                      disabled={readOnly}
                    />
                  </div>
                </>
              ) : (
                <div className="grid grid-cols-2 items-center gap-4">
                  <Label className="text-left">Network Name</Label>
                  <Input
                    value={bss2.ssid}
                    onChange={(e) => updateBothBands({ ssid: e.target.value })}
                    placeholder="e.g. Test"
                    maxLength={32}
                    disabled={readOnly}
                  />
                </div>
              )}

              <div className="grid grid-cols-2 items-center gap-4">
                <Label className="text-left">Wifi Security</Label>
                <Select
                  value={activeAuthType}
                  onValueChange={(value) => updateSecurityType(value as WifiAuthType)}
                  disabled={readOnly}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Wifi Security" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={WifiAuthType.WPA2}>WPA2</SelectItem>
                    <SelectItem value={WifiAuthType.WPA3}>WPA3</SelectItem>
                    <SelectItem value={WifiAuthType.WPA2_WPA3}>WPA2/WPA3</SelectItem>
                    <SelectItem value={WifiAuthType.WPA3_OWE}>WPA3-OWE</SelectItem>
                    <SelectItem value={WifiAuthType.OPEN}>OPEN</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {showPasswordField && (
                <div className="grid grid-cols-2 items-center gap-4">
                  <Label className="text-left">Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={sharedPassword}
                      onChange={(e) => updateSharedPassword(e.target.value)}
                      placeholder="8–63 characters"
                      disabled={readOnly}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </section>

          <Separator />

          <section className="space-y-3 rounded-lg border p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold">Networking Options</h3>
                <p className="text-xs text-muted-foreground">Configure networking options for the router.</p>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <Label htmlFor="hidden">Hidden</Label>
                <p className="text-xs text-muted-foreground">Hide the network from other devices.</p>
              </div>
              <Switch
                id="hidden"
                checked={bss2.hidden}
                onCheckedChange={(checked) => updateHiddenBoth(checked)}
                disabled={readOnly}
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <Label htmlFor="clientIsolation">Client isolation</Label>
                <p className="text-xs text-muted-foreground">Prevent Wi-Fi clients from talking to each other.</p>
              </div>
              <Switch
                id="clientIsolation"
                checked={settings.networks?.[activeNetworkIndex]?.clientIsolation ?? false}
                onCheckedChange={(checked) => {
                  updateSettings((current) => {
                    const networks = [...(current.networks ?? [])];
                    while (networks.length <= activeNetworkIndex) {
                      networks.push(defaultNetwork());
                    }
                    const network = networks[activeNetworkIndex] ?? defaultNetwork();
                    networks[activeNetworkIndex] = { ...network, clientIsolation: checked };
                    return { ...current, networks };
                  });
                }}
                disabled={readOnly}
              />
            </div>
          </section>

          <Separator />

          <section className="space-y-3 rounded-lg border p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold">General Settings</h3>
                <p className="text-xs text-muted-foreground">Configure general settings for the router.</p>
              </div>
            </div>
            <div className="grid gap-2 md:grid-cols-2">
              {MODE_FLAGS.map(renderFlag)}
            </div>
          </section>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
              {isView ? 'Close' : 'Cancel'}
            </Button>
            {isView ? (
              onDuplicate && (
                <Button type="button" className="gap-2" onClick={onDuplicate}>
                  <CopyIcon className="h-4 w-4" />
                  Duplicate
                </Button>
              )
            ) : (
              <Button type="submit" disabled={pending} className="gap-2">
                {pending && <Loader2 className="h-4 w-4 animate-spin" />}
                {isDuplicate ? 'Create Duplicate' : 'Create Config'}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RouterConfigDialog;
